import { useState } from "react";
import Modal from "components/DesignSystem/Modal/Modal";
import { Button } from "components/DesignSystem/Button/Button";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import {
  TaxFiling,
  useMarkFilingFiledWithCopyMutation,
} from "store/apis/filing";
import { FilingFiledCopyRow } from "./FilingFiledCopyRow";

export const MarkFilingsFiledModal = ({
  open,
  onClose,
  filings,
  entityId,
  onFiled,
}: {
  open: boolean;
  onClose: () => void;
  filings: TaxFiling[];
  entityId: string;
  onFiled: () => void;
}) => {
  const [filedCopyFiles, setFiledCopyFiles] = useState<Record<string, File>>(
    {}
  );
  const [markFilingFiledWithCopy, { isLoading }] =
    useMarkFilingFiledWithCopyMutation();
  const { alertToast, successToast } = useToast();

  const handleFileChange = (filingId: string, file: File | null) => {
    setFiledCopyFiles((prev) => {
      const next = { ...prev };
      if (file) {
        next[filingId] = file;
      } else {
        delete next[filingId];
      }
      return next;
    });
  };

  const handleMarkAsFiled = async () => {
    try {
      await Promise.all(
        filings.map((filing) =>
          markFilingFiledWithCopy({
            entityId,
            filingId: filing.uuid,
            filedCopyFile: filedCopyFiles[filing.uuid] ?? null,
          }).unwrap()
        )
      );
      successToast({ message: "Filings marked as filed." });
      setFiledCopyFiles({});
      onFiled();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ??
          "Something went wrong",
      });
    }
  };

  const handleClose = () => {
    setFiledCopyFiles({});
    onClose();
  };

  return (
    <Modal.Root open={open} onOpenChange={handleClose}>
      <Modal.Content
        useCustomOverlay
        size="xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
      >
        <Modal.Header>
          <Modal.Title>Mark as filed</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <p className="t-text-text-30 t-text-body-sm t-mb-4">
            Optionally attach the filed copy for each filing before confirming.
          </p>
          <div className="t-flex t-flex-col">
            <div className="t-grid t-grid-cols-2 t-gap-4 t-pb-2 t-border-0 t-border-b t-border-solid t-border-neutral-0">
              <p className="t-m-0 t-text-body-sm t-text-text-30">State</p>
              <p className="t-m-0 t-text-body-sm t-text-text-30">Filed copy</p>
            </div>
            {filings.map((filing) => (
              <FilingFiledCopyRow
                key={filing.uuid}
                stateName={filing.state}
                periodStartDate={filing.filing_period_start_date}
                periodEndDate={filing.filing_period_end_date}
                file={filedCopyFiles[filing.uuid] ?? null}
                onFileChange={(file) => handleFileChange(filing.uuid, file)}
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            customType="primary"
            onClick={() => void handleMarkAsFiled()}
            isLoading={isLoading}
            disabled={isLoading}
          >
            Mark as filed
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};
